import { assert, executeOnce, objectEntries, type ReadonlyRecord } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type SortableColumn, type SortConfiguration } from '../../../../domain'

/**
 * This type represents a map of domain to backend supported sorting columns
 *
 * Null entries are considered as not supported columns
 */
export type SortableColumnsMap<Domain, OrderByColumns extends string> = ReadonlyRecord<SortableColumn<Domain>, OrderByColumns | null>

type SortQueryParameters<OrderByColumns> = Readonly<{ order?: 'ASC' | 'DESC', orderBy?: OrderByColumns }>

export const createSortMapper = <Domain, OrderBy extends string>(
    sortableColumns: SortableColumnsMap<Domain, OrderBy>
): (sort: SortConfiguration<Domain>) => SortQueryParameters<OrderBy> => {
    const getSupportedColumns = executeOnce(() =>
        objectEntries(sortableColumns).reduce(
            (m, [domainColumn, backendColumn]) => (backendColumn ? m.set(domainColumn, backendColumn) : m),
            new Map<SortConfiguration<Domain>['column'], OrderBy>()
        )
    )

    return (sort) => {
        const supportedColumns = getSupportedColumns()

        const orderBy = supportedColumns.get(sort.column)

        assert(
            orderBy,
            new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Sorting by this column is not supported', {
                sort: sort.column,
                sortable: Array.from(supportedColumns.keys()).sort(),
            })
        )

        return { order: sort.asc ? 'ASC' : 'DESC', orderBy }
    }
}
